import { useEffect, useState } from 'react';
import { Button, Dialog } from '@dataroom/shared';
import type { CategoryNode, RoomDocument } from '@dataroom/shared';
import { CategoryPicker } from './CategoryPicker';

interface ModifyDocumentAttributesDialogProps {
  /** The document being edited, or null when the dialog is closed. */
  document: RoomDocument | null;
  /** The room's category hierarchy (the selectable category tags). */
  categories: CategoryNode[];
  /** Whether the submit is in flight. */
  pending: boolean;
  /** A server/validation error to surface, or null. */
  error: string | null;
  onClose: () => void;
  onSubmit: (categoryTagIds: string[]) => void;
}

/**
 * "Modify Document Attributes" modal. Presentational: it owns
 * only the working checkbox selection (seeded from the document's current
 * category tags each time it opens); the page owns which document is open, the
 * mutation, and the pending/error state. Phase A edits the category tags only —
 * renaming is not offered because the backend has no document-level display
 * column (the only filename is the immutable per-version one).
 */
export function ModifyDocumentAttributesDialog({
  document,
  categories,
  pending,
  error,
  onClose,
  onSubmit,
}: ModifyDocumentAttributesDialogProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Re-seed the working selection from the document's current category tags
  // whenever a different document is opened.
  useEffect(() => {
    setSelected(new Set(document?.categoryTagIds ?? []));
  }, [document]);

  if (!document) return null;

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // The shared Dialog: labelled heading, focus in + trapped + returned to the
  // row's ⋯ menu on close, Escape (topmost only) and scrim close.
  return (
    <Dialog
      title="Modify document attributes"
      description={document.name}
      onClose={onClose}
      busy={pending}
      footer={
        <>
          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : (
            <span className="text-xs text-muted-foreground" aria-live="polite">
              {selected.size} categor{selected.size === 1 ? 'y' : 'ies'} selected
            </span>
          )}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
            <Button size="sm" onClick={() => onSubmit([...selected])} disabled={pending}>
              {pending ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </>
      }
    >
      <p className="mb-3 text-sm font-medium">Category tags</p>
      <CategoryPicker categories={categories} selected={selected} onToggle={toggle} />
    </Dialog>
  );
}
